import { Badge, Box, Button, Flex, Group, Text } from '@mantine/core';
import {
  IconFiles,
  IconPencil,
  IconPlus,
  IconPower,
  IconTool,
  IconTrash,
} from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { SkillProposedAction, SkillSettingChange } from '../chat/types';
import { formatSkillName } from 'src/common/utils/skillNames';
import { ProposalDecidedLine } from './ProposalDecidedLine';
import { ProposalDiffBlock } from './ProposalDiffBlock';

type Props = {
  proposal: SkillProposedAction;
  onDecide: (approve: boolean) => void;
  disabled?: boolean;
};

const OPERATION_ICONS = {
  CREATE: IconPlus,
  UPDATE: IconPencil,
  UPDATE_FILES: IconFiles,
  DELETE: IconTrash,
  SET_ENABLED: IconPower,
} as const;

// The suitability and enabled labels are the editor's own, so an adviser recognises the switch they
// are approving; the editors word the other settings as questions or developer labels.
const SETTING_LABELS: Record<SkillSettingChange['setting'], string> = {
  requires_sandbox: 'Creates or edits files',
  file_explorer_enabled: "Reads the client's uploaded documents",
  company_files_visible: 'Sees your company documents',
  can_generate_suitability_reports: 'Allow Siesta to generate suitability reports',
  is_enabled: 'Enabled',
};

const onOff = (value: boolean) => (value ? 'on' : 'off');

const describeSettingChange = ({ setting, before, after }: SkillSettingChange) =>
  before === null
    ? `${SETTING_LABELS[setting]}: ${onOff(after)}`
    : `${SETTING_LABELS[setting]}: ${onOff(before)} → ${onOff(after)}`;
const describeFileCount = (proposal: SkillProposedAction): string | null => {
  if (proposal.fileCount === 0) return null;
  const files = `${proposal.fileCount} supporting ${proposal.fileCount === 1 ? 'file' : 'files'}`;
  return proposal.operation === 'UPDATE_FILES' ? `Changes ${files}` : `Also changes ${files}`;
};

export function SkillProposalWidget({ proposal, onDecide, disabled }: Props) {
  const isPending = proposal.status === 'PENDING' || !proposal.status;
  const OperationIcon = OPERATION_ICONS[proposal.operation];
  const showDiff = proposal.operation !== 'SET_ENABLED' && proposal.diffLines.length > 0;
  const settingChanges = proposal.settingChanges ?? [];
  const fileCount = describeFileCount(proposal);

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap">
          <IconTool size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            Siesta wants to change a skill
          </Text>
          <Badge size="sm" variant="light" color="warning" style={{ flexShrink: 0 }}>
            Firm-wide
          </Badge>
        </Group>
        {/* Everything below the summary is computed server-side from the stored proposal,
            never from Siesta's own description of what it did. */}
        <Group gap="xs" align="flex-start" wrap="nowrap">
          {/* Nudged down to sit on the first line of the summary, which can wrap. */}
          <OperationIcon
            size={16}
            stroke={1.5}
            color="var(--mantine-color-orange-7)"
            style={{ flexShrink: 0, marginTop: 2 }}
            aria-hidden
          />
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
              {proposal.summary}
            </Text>
            <Text size="textXs" c="gray.7">
              {proposal.newName
                ? `Renames '${formatSkillName(proposal.skillName)}' to '${formatSkillName(proposal.newName)}'`
                : formatSkillName(proposal.skillName)}
            </Text>
            {fileCount && (
              <Text size="textXs" c="gray.7">
                {fileCount}
              </Text>
            )}
          </Box>
          {showDiff && (
            <Text size="textXs" c="gray.6">
              +{proposal.addedCount} −{proposal.removedCount}
            </Text>
          )}
        </Group>
        {settingChanges.length > 0 && (
          <Box>
            {settingChanges.map(change => (
              <Text key={change.setting} size="textXs" c="gray.8">
                {describeSettingChange(change)}
              </Text>
            ))}
          </Box>
        )}
        {showDiff && (
          <ProposalDiffBlock
            lines={proposal.diffLines}
            truncated={proposal.diffTruncated}
            defaultCollapsed
          />
        )}
        {isPending ? (
          <Group gap="sm">
            <Button
              variant="filled"
              color="orange"
              size="sm"
              onClick={() => onDecide(true)}
              disabled={disabled}
            >
              Approve
            </Button>
            <Button variant="default" size="sm" onClick={() => onDecide(false)} disabled={disabled}>
              Deny
            </Button>
          </Group>
        ) : (
          <ProposalDecidedLine status={proposal.status} failureReason={proposal.failureReason} />
        )}
      </Flex>
    </Box>
  );
}
